import React, { useEffect, useMemo, useState } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ExtensionRoundedIcon from "@mui/icons-material/ExtensionRounded";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import StepPlan from "./StepPlan";
import StepAddOns from "./StepAddOns";
import StepSupport from "./StepSupport";

// ----------------------------------------------------------------------
//   Wizard · Step "Suscripción" agrupador. Combina 3 sub-steps (Plan,
//   Extras, Soporte) en un único step del wizard con tabs internas —
//   antes eran 3 steps separados, lo que hacía el wizard demasiado
//   largo (13 pasos). Ahora son 3 tabs dentro del mismo step.
//
//   Comportamiento:
//     · Pestaña Plan (default): PlanPicker + toggle mes/año.
//     · Pestaña Extras: AddOnsPicker con lógica "N gratis según plan".
//     · Pestaña Soporte: SupportPicker (radio Básico/Estándar/Prioritario).
//
//   Cada tab persiste su parte en `pendingSelection` de forma
//   independiente. Al pulsar "Guardar y continuar" en cualquiera se
//   avanza el step COMPLETO — el user no está forzado a visitar las 3
//   tabs (Extras y Soporte son opcionales). Si necesita cambiarlas
//   después, el chip clicable del stepper le trae de vuelta al step
//   Suscripción y las tabs siguen accesibles.
// ----------------------------------------------------------------------

const ALL_TABS = [
  { key: "plan", label: "Plan", icon: <WorkspacePremiumRoundedIcon fontSize="small" />, component: StepPlan },
  { key: "addons", label: "Extras", icon: <ExtensionRoundedIcon fontSize="small" />, component: StepAddOns },
  { key: "support", label: "Soporte", icon: <HeadsetMicRoundedIcon fontSize="small" />, component: StepSupport },
];

export default function StepSubscription({ progress, onComplete, onSkip }) {
  const [tab, setTab] = useState("plan");
  const { addOns } = useSelector((s) => s.addOns);

  // Ocultamos tabs (Extras / Soporte) cuya sección no tenga ningún
  // AddOn activo del catálogo. Sin esto, si el admin no ha subido
  // extras/soporte al catálogo el user veía dos pestañas vacías con
  // "Aún no hay extras disponibles" — desconcertante y añade fricción.
  // Plan siempre visible (siempre hay al menos un plan configurable).
  const TABS = useMemo(() => {
    const active = (addOns || []).filter((a) => a.isActive !== false);
    const hasExtras = active.some((a) => !a.kind?.startsWith("support"));
    const hasSupport = active.some((a) => a.kind?.startsWith("support"));
    return ALL_TABS.filter((t) => {
      if (t.key === "addons") return hasExtras;
      if (t.key === "support") return hasSupport;
      return true;
    });
  }, [addOns]);

  // Si el tab activo desaparece del filtro (por ejemplo, admin desactivó
  // todos los addons de extras mientras el user estaba en esa pestaña),
  // rebotamos a Plan para que no quede un tab "vacío" seleccionado.
  useEffect(() => {
    if (!TABS.find((t) => t.key === tab)) {
      setTab("plan");
    }
  }, [TABS, tab]);

  const Active = TABS.find((t) => t.key === tab)?.component || StepPlan;

  // Wrappers para que "Continuar" y "Saltar" dentro del sub-step
  // avancen de tab en tab en lugar de cerrar el step wrapper. Cuando
  // el user llega a la última tab (Soporte), Continuar / Saltar sí
  // completan el step entero y el wizard salta al siguiente.
  const currentIdx = TABS.findIndex((t) => t.key === tab);
  const isLastTab = currentIdx === TABS.length - 1;

  const handleInnerComplete = (extra) => {
    if (!isLastTab) {
      setTab(TABS[currentIdx + 1].key);
      return;
    }
    onComplete?.(extra);
  };
  const handleInnerSkip = () => {
    if (!isLastTab) {
      setTab(TABS[currentIdx + 1].key);
      return;
    }
    onSkip?.();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Configura tu suscripción
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Elige plan, extras y nivel de soporte. Nada se cobra durante los 30
        días de prueba — puedes cambiarlos en cualquier momento desde
        Ajustes → Facturación.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            icon={t.icon}
            iconPosition="start"
            label={t.label}
            sx={{ fontWeight: 700, minHeight: 48 }}
          />
        ))}
      </Tabs>

      <Active
        progress={progress}
        onComplete={handleInnerComplete}
        onSkip={handleInnerSkip}
      />
    </Box>
  );
}
